/*
 * Elevar Data Layer Events
 *
 * Available Events:
 * - DONE pushUserData(cartTotal?, products?) - User data and cart contents
 * - DONE pushViewItem(product, list?) - Product view tracking (Subscription only)
 * - DONE pushViewItemList(products, list?) - Product list view tracking (for /subscriptions page)
 * - DONE pushAddToCart(product, quantity, list?) - Add to cart tracking (subscription only)
 * - DONE pushViewCart(cart) - Cart view tracking
 * - pushLogin() - Login event tracking
 * - pushSubscribe(email?, phone?) - Newsletter subscription tracking
 */

import { getSession } from 'next-auth/react'
import type { Product, ProductVariant } from '@lib/types/product'
import type { Cart, CartItem } from '@lib/types/cart'

// Initialize Elevar data layer
declare global {
	interface Window {
		ElevarDataLayer: any[]
		ElevarInvalidateContext?: () => void
		// ElevarDebugMode: (enabled: boolean) => void
	}
}

// Track last event to prevent duplicates
let lastEvent: { type: string; data: any; timestamp: number } | null = null

// Track last route to prevent duplicate fires
let lastRoute: string | null = null
let isInitialLoad = true

// Event queue system
interface QueuedEvent {
	type: string
	data: any
	timestamp: number
}

const eventQueue: QueuedEvent[] = []
let isProcessingQueue = false

// Process events sequentially
const processEventQueue = async () => {
	if (isProcessingQueue || eventQueue.length === 0) return

	isProcessingQueue = true

	try {
		const event = eventQueue[0]

		// Invalidate context before processing event
		if (window.ElevarInvalidateContext) {
			window.ElevarInvalidateContext()
		}

		// Wait for context invalidation
		await new Promise<void>((resolve) => {
			setTimeout(() => resolve(), 100)
		})

		// Push event to data layer
		window.ElevarDataLayer.push(event.data)

		// Remove processed event
		eventQueue.shift()

		// Process next event if any
		if (eventQueue.length > 0) {
			await processEventQueue()
		}
	} finally {
		isProcessingQueue = false
	}
}

// Helper to prevent duplicate events
const preventDuplicateEvent = (type: string, data: any): boolean => {
	const now = Date.now()

	// Check if this is a duplicate event within 1 second
	if (
		lastEvent &&
		lastEvent.type === type &&
		JSON.stringify(lastEvent.data) === JSON.stringify(data) &&
		now - lastEvent.timestamp < 1000
	) {
		// console.log(`[Elevar] Skipping duplicate ${type} event within 1 second`)
		return true
	}

	// Update last event
	lastEvent = { type, data, timestamp: now }
	return false
}

interface UserProperties {
	visitor_type: 'guest' | 'logged_in'
	customer_email?: string
	customer_id?: string
	customer_first_name?: string
	customer_last_name?: string
	customer_phone?: string
	customer_address_1?: string
	customer_address_2?: string
	customer_city?: string
	customer_province?: string
	customer_province_code?: string
	customer_zip?: string
	customer_country?: string
	customer_order_count?: string
	customer_total_spent?: string
	customer_tags?: string
}

interface ElevarProduct {
	id: string
	name: string
	brand: string
	category: string
	variant: string
	price: string
	quantity?: string
	product_id: string
	variant_id: string
	compare_at_price: string
	image: string
	url?: string
	position?: number
	list?: string
}

// Initialize the data layer if it doesn't exist
if (typeof window !== 'undefined') {
	window.ElevarDataLayer = window.ElevarDataLayer ?? []
}

// Helper to get user properties
const getUserProperties = async (): Promise<UserProperties> => {
	const session = await getSession()

	if (!session?.user) {
		return {
			visitor_type: 'guest' as const
		}
	}

	return {
		visitor_type: 'logged_in' as const,
		customer_email: session.user.email,
		customer_id: session.user.customer?.id,
		customer_first_name: session.user.customer?.firstName,
		customer_last_name: session.user.customer?.lastName,
		customer_phone: session.user.customer?.phone,
		customer_address_1: session.user.customer?.address1,
		customer_address_2: session.user.customer?.address2,
		customer_city: session.user.customer?.city,
		customer_province: session.user.customer?.province,
		customer_province_code: session.user.customer?.provinceCode,
		customer_zip: session.user.customer?.zip,
		customer_country: session.user.customer?.country,
		customer_order_count: session.user.customer?.orderCount,
		customer_total_spent: session.user.customer?.totalSpent,
		customer_tags: session.user.customer?.tags?.join(', ') || '' // Convert array to comma-separated string
	}
}

// Helper to push events to data layer with validation
const pushToDataLayer = async (eventData: any) => {
	//   console.log('[Elevar] Queueing event:', {
	//     event: eventData.event,
	//     timestamp: new Date().toISOString(),
	//     data: JSON.stringify(eventData, null, 2)
	//   })

	// Validate required fields
	if (!eventData.event) {
		console.error('[Elevar] Event missing required field: event')
		return
	}

	if (eventData.ecommerce) {
		if (!eventData.ecommerce.currencyCode) {
			console.error('[Elevar] Event missing required field: ecommerce.currencyCode')
			return
		}

		if (eventData.ecommerce.products) {
			eventData.ecommerce.products.forEach((product: any, index: number) => {
				const requiredFields = ['id', 'name', 'price']
				const missingFields = requiredFields.filter((field) => !product[field])
				if (missingFields.length > 0) {
					console.error(
						`[Elevar] Product at index ${index} missing required fields:`,
						missingFields
					)
				}
			})
		}
	}

	// Add event to queue
	eventQueue.push({
		type: eventData.event,
		data: eventData,
		timestamp: Date.now()
	})

	// Start processing queue if not already processing
	if (!isProcessingQueue) {
		await processEventQueue()
	}
}

// Base event that should be fired before any other event
export const pushUserData = async (cartTotal?: string, products?: ElevarProduct[]) => {
	const currentRoute = window.location.pathname

	// Skip if we're on the same route and it's not the initial load
	if (currentRoute === lastRoute && !isInitialLoad) {
		// console.log('[Elevar] Skipping user data event - same route:', currentRoute)
		return
	}

	// Update route tracking
	lastRoute = currentRoute
	isInitialLoad = false

	const userProperties = await getUserProperties()

	const eventData = {
		event: 'dl_user_data',
		cart_total: cartTotal || '0.00',
		user_properties: userProperties,
		ecommerce: {
			currencyCode: 'USD',
			cart_contents: {
				products: products || []
			}
		}
	}

	// Push user data immediately
	// console.log('[Elevar] Session data:', JSON.stringify(await getSession(), null, 2))
	// console.log('[Elevar] User properties:', JSON.stringify(userProperties, null, 2))
	await pushToDataLayer(eventData)
}

// Product view event
export const pushViewItem = async (product: Product, list?: string) => {
	const userProperties = await getUserProperties()
	const variant = product.variants[0] // Use first variant for now

	// Extract numeric IDs from Shopify GIDs
	const productId = product.id.split('/').pop() || ''
	const variantId = variant?.id.split('/').pop() || ''

	const eventData = {
		event: 'dl_view_item',
		user_properties: userProperties,
		ecommerce: {
			currencyCode: 'USD',
			detail: {
				actionField: {
					list: list || window.location.pathname,
					action: 'detail'
				},
				products: [
					{
						id: product.id,
						name: product.name,
						brand: product.vendor || '',
						category: product.type,
						variant: variant?.title || 'Default', // Fixed: Use 'Default' as fallback for empty variant titles
						price: variant?.price?.amount || '0.0',
						product_id: productId, // Use numeric ID from GID
						variant_id: variantId, // Use numeric ID from GID
						compare_at_price: variant?.compareAtPrice?.amount || '0.0',
						image: product.images[0]?.url || 'https://www.birchbox.com/images/default-product.jpg', // Provide default image
						url: window.location.href,
						list: list || window.location.pathname // Add list to product object
					}
				]
			}
		}
	}

	// Skip if this is a duplicate event
	if (preventDuplicateEvent('dl_view_item', eventData)) {
		return
	}

	// console.log('[Elevar] Pushing view item event:', JSON.stringify(eventData, null, 2))
	await pushToDataLayer(eventData)
}

// Add to cart event
export const pushAddToCart = async (product: Product, quantity: number, list?: string) => {
	const userProperties = await getUserProperties()
	const variant = product.variants?.[0]

	// Extract numeric IDs from Shopify GIDs
	const productId = product.id.split('/').pop() || ''
	const variantId = variant?.id.split('/').pop() || ''

	const eventData = {
		event: 'dl_add_to_cart',
		user_properties: userProperties,
		ecommerce: {
			currencyCode: 'USD',
			add: {
				actionField: {
					list: list || window.location.pathname
				},
				products: [
					{
						id: product.id,
						name: product.name,
						brand: product.vendor || '',
						category: product.type || 'product',
						variant: variant?.title || 'Default', // Fixed: Use 'Default' as fallback for empty variant titles
						price: variant?.price?.amount || product.price?.amount || '0.0',
						quantity: quantity.toString(),
						product_id: productId, // Use numeric ID from GID
						variant_id: variantId, // Use numeric ID from GID
						compare_at_price: variant?.compareAtPrice?.amount || '0.0',
						image:
							product.images?.[0]?.url || 'https://www.birchbox.com/images/default-product.jpg',
						url: window.location.href,
						list: list || window.location.pathname // Add list to product object
					}
				]
			}
		}
	}

	// Skip if this is a duplicate event
	if (preventDuplicateEvent('dl_add_to_cart', eventData)) {
		return
	}

	// console.log('[Elevar] Pushing add to cart event:', JSON.stringify(eventData, null, 2))
	await pushToDataLayer(eventData)
}

// View cart event
export const pushViewCart = async (cart: Cart) => {
	const userProperties = await getUserProperties()

	// Debug cart data
	// console.log('[Elevar] Cart data:', JSON.stringify(cart, null, 2))

	// Get cart lines from the edges structure
	const cartLines = cart.lines.edges.map((edge) => edge.node)
	// console.log('[Elevar] Cart lines:', JSON.stringify(cartLines, null, 2))

	const eventData = {
		event: 'dl_view_cart',
		user_properties: userProperties,
		cart_total: cart.estimatedCost.totalAmount.amount.toString(),
		ecommerce: {
			currencyCode: 'USD',
			actionField: {
				list: 'Shopping Cart' // Fixed: Must be exactly "Shopping Cart"
			},
			impressions: cartLines.map((item: CartItem, index: number) => {
				// console.log('[Elevar] Processing cart item:', JSON.stringify(item, null, 2))

				// Extract numeric IDs from Shopify GIDs
				const productId = item.merchandise.product.id.split('/').pop() || ''
				const variantId = item.merchandise.id.split('/').pop() || ''

				return {
					id: item.merchandise.id,
					name: item.merchandise.product.title,
					brand: item.merchandise.product.vendor,
					category: 'product',
					variant: item.merchandise.title,
					price: item.estimatedCost.subtotalAmount.amount,
					quantity: item.quantity.toString(),
					product_id: productId, // Fixed: Using numeric ID from GID
					variant_id: variantId, // Fixed: Using numeric ID from GID
					compare_at_price: '0.0',
					position: index + 1,
					image: item.merchandise.image?.url || '',
					url: `${window.location.origin}/products/${item.merchandise.product.handle}`
				}
			})
		}
	}

	// Skip if this is a duplicate event
	if (preventDuplicateEvent('dl_view_cart', eventData)) {
		return
	}

	// console.log('[Elevar] Pushing view cart event:', JSON.stringify(eventData, null, 2))
	await pushToDataLayer(eventData)
}

// Login event
export const pushLogin = async () => {
	const userProperties = await getUserProperties()

	const eventData = {
		event: 'dl_login',
		user_properties: userProperties
	}

	// console.log('[Elevar] Pushing login event:', JSON.stringify(eventData, null, 2))
	pushToDataLayer(eventData)
}

// Sign up event
// export const pushSignUp = async () => {
//   const userProperties = await getUserProperties()

//   const eventData = {
//     event: 'dl_sign_up',
//     user_properties: userProperties
//   }

//   console.log('[Elevar] Pushing sign up event:', JSON.stringify(eventData, null, 2))
//   pushToDataLayer(eventData)
// }

// Newsletter subscription event
export const pushSubscribe = (email?: string, phone?: string) => {
	if (email) {
		const eventData = {
			event: 'dl_subscribe',
			lead_type: 'email',
			user_properties: {
				customer_email: email
			}
		}

		// console.log('[Elevar] Pushing email subscribe event:', JSON.stringify(eventData, null, 2))
		pushToDataLayer(eventData)
	}

	if (phone) {
		const eventData = {
			event: 'dl_subscribe',
			lead_type: 'phone',
			user_properties: {
				customer_phone: phone
			}
		}

		// console.log('[Elevar] Pushing phone subscribe event:', JSON.stringify(eventData, null, 2))
		pushToDataLayer(eventData)
	}
}

// Enable/disable Elevar debugger
// export const setElevarDebugMode = (enabled: boolean) => {
//   if (typeof window !== 'undefined') {
//     window?.ElevarInvalidateContext?.()
//     window.ElevarDebugMode(enabled)
//     console.log(`[Elevar] Debug mode ${enabled ? 'enabled' : 'disabled'}`)
//   }
// }

// Invalidate context on route changes
export const invalidateContext = () => {
	if (window.ElevarInvalidateContext) {
		// console.log('[Elevar] Invalidating context')
		window.ElevarInvalidateContext()
	}
}
